import { useContext, useEffect, useMemo, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'

import { AppContext } from '/providers/app-context'
import { createTableStyles } from './table-styles'

export const useStudioTable = ({ mrtTheme, tableKey, ...options }) => {
  const theme = useTheme()
  const { user, saveUser } = useContext(AppContext)
  const storedVisibility = tableKey ? user?.preferences?.table_column_visibility?.[tableKey] : undefined
  const defaultVisibility = options.state?.columnVisibility ?? options.initialState?.columnVisibility ?? {}
  const storedVisibilityKey = JSON.stringify(storedVisibility ?? null)
  const defaultVisibilityKey = JSON.stringify(defaultVisibility)
  const [columnVisibility, setColumnVisibility] = useState(storedVisibility ?? defaultVisibility)

  useEffect(() => {
    setColumnVisibility(storedVisibility ?? defaultVisibility)
  }, [tableKey, user?.user_id, storedVisibilityKey, defaultVisibilityKey])

  const handleColumnVisibilityChange = updater => {
    const nextVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater
    setColumnVisibility(nextVisibility)

    if (tableKey && user && saveUser) {
      saveUser({
        ...user,
        preferences: {
          ...(user.preferences ?? {}),
          table_column_visibility: {
            ...(user.preferences?.table_column_visibility ?? {}),
            [tableKey]: nextVisibility
          }
        }
      })
    }
  }

  const tableStyles = useMemo(() => createTableStyles(theme), [theme])
  const mergedPaperProps = mergeMrtSlotProps(tableStyles.muiTablePaperProps, options.muiTablePaperProps)
  const mergedContainerProps = mergeMrtSlotProps(tableStyles.muiTableContainerProps, options.muiTableContainerProps)
  const mergedBodyRowProps = mergeMrtSlotProps(tableStyles.muiTableBodyRowProps, options.muiTableBodyRowProps)
  const mergedBodyCellProps = mergeMrtSlotProps(tableStyles.muiTableBodyCellProps, options.muiTableBodyCellProps)

  return useMaterialReactTable({
    ...tableStyles,
    ...options,
    state: {
      ...options.state,
      columnVisibility
    },
    onColumnVisibilityChange: handleColumnVisibilityChange,
    muiTablePaperProps: mergedPaperProps,
    muiTableContainerProps: mergedContainerProps,
    muiTableBodyRowProps: mergedBodyRowProps,
    muiTableBodyCellProps: mergedBodyCellProps,
    mrtTheme: _muiTheme => ({
      // MRT requires a parseable colour here. rgba() keeps unselected rows
      // genuinely transparent while allowing MRT to derive selection layers.
      baseBackgroundColor: 'rgba(0, 0, 0, 0)',
      ...(typeof mrtTheme === 'function' ? mrtTheme(_muiTheme) : mrtTheme),
      // MRT otherwise adds a primary-coloured td:after overlay to checked rows.
      // Selection colour is owned by the shared row/cell styles instead.
      selectedRowBackgroundColor: 'rgba(0, 0, 0, 0)'
    })
  })
}

const StudioTable = ({ table }) => <MaterialReactTable table={table} />

export default StudioTable

const mergeMrtSlotProps = (baseProps, overrideProps) => {
  if (!overrideProps) return baseProps

  const merge = args => {
    const base = typeof baseProps === 'function' ? baseProps(args) : baseProps
    const override = typeof overrideProps === 'function' ? overrideProps(args) : overrideProps
    return {
      ...base,
      ...override,
      sx: [base?.sx, override?.sx].filter(Boolean),
    }
  }

  return typeof baseProps === 'function' || typeof overrideProps === 'function'
    ? merge
    : merge(undefined)
}
